/**
 * MUI TableConstants that replaces the TableConstants exported from Table.js
 * 
 * This file provides a Material UI equivalent of the original TableConstants
 * with the same structure and functionality.
 */
import { quadraticBezier, Point } from "utilities/mathematic";
import { floor, range, isEmpty } from "lodash";
import { TableSize, ChairConfig, ChairArrayProps, TablePathProps, DevicePathProps, TableBox } from "types/components/elements";

/**
 * @constant MuiTableConstants
 * @description Contains various constants and configuration objects for table and chair properties.
 * @property {Object} tables - Configurations for different table shapes (rectangle, racetrack, boat, etc.)
 * @property {Object} chair - Constants for chair properties (size, clearance, state)
 * @property {Object} microphones - Constants for microphone positioning
 */
export const MuiTableConstants = {
  tables: {
    rectangle: {
      pathProps: ({ length, width }: TableSize): TablePathProps => {
        const corner = Math.min(length * 0.03, width * .15)
        return {
          corner: corner,
          length: length - 2 * corner, //  * .94,
          width: width - 2 * corner //length * 0.06,
        }
      },
      chairs: ({ length, width }: TableSize = { length: 0, width: 0 }, chair: ChairConfig): ChairArrayProps => {
        // Seat at least one chair per side. When a side is shorter than a chair
        // (e.g. a narrow U-shape end after the width adjustment), floor() returns 0,
        // which collapses the spacing and stacks the end chair off-center.
        const qtyX = Math.max(1, floor(length / chair.size));
        const spaceX = length / qtyX;
        const firstX = (spaceX - length) / 2;
        const y = width / 2 + chair.clearance;
        const chairs = range(firstX, length / 2, spaceX).flatMap((x, i) => [
          { id: `chair-bottom${i}`, x: x, y: y, a: 90 },
          { id: `chair-top${i}`, x: x, y: -y, a: -90 }
        ]);
        const qtyY = Math.max(1, floor(width / chair.size));
        const spaceY = width / qtyY;
        const firstY = (spaceY - width) / 2;
        const x = length / 2 + chair.clearance;
        const chairArray = chairs.concat(
          range(firstY, width / 2, spaceY).flatMap((y, i) => [
            { id: `chair-right${i}`, x: x, y: y, a: 0 }, 
            { id: `chair-left${i}`, x: -x, y: y, a: 180 }
          ])
        );
        return {
          chairArray,
          chairSpacing: { x: spaceX, y: spaceY }
        }
      },
      devicePathProps: (
        tableSize: TableSize, 
        chairSpacing: { x: number, y: number }, 
        tableBox: TableBox | null, 
        deviceFromEdge: number, 
        deviceCoverageRadius: number
      ): DevicePathProps => {
        let devicePathLength = tableSize.length - 2 * deviceFromEdge;
        let devicePathWidth = tableSize.width - 2 * deviceFromEdge;
        if (!isEmpty(tableBox) && deviceCoverageRadius * 2 >= (tableBox as TableBox).x) {
          devicePathLength = 0;
          devicePathWidth = Math.floor(devicePathWidth / chairSpacing.y) * chairSpacing.y
        };
        if (!isEmpty(tableBox) && deviceCoverageRadius * 2 >= (tableBox as TableBox).y) {
          devicePathWidth = 0;
          devicePathLength = Math.floor(devicePathLength / chairSpacing.x) * chairSpacing.x
        };
        return { devicePathLength, devicePathWidth }
      },
    },
    racetrack: {
      pathProps: ({ length, width }: TableSize): TablePathProps => ({
        corner: (width <= length ? width : length) / 2,
        length: width <= length ? length - width : width - length,
        width: 0,
      }),
      chairs: (tableSize: TableSize, chair: ChairConfig): ChairArrayProps => {
        const length = Math.max(...Object.values(tableSize));
        const width = Math.min(...Object.values(tableSize));
        const radius = width / 2;
        const straightLength = length - width; // Length of straight sections combined
        const halfStraight = straightLength / 2;
        const chairRadius = radius + chair.clearance;

        // Calculate chairs for straight sections
        let straightSectionChairs = [];

        // Number of chairs on straight sections (top and bottom)
        const qtyX = floor(straightLength / chair.size);
        if (qtyX > 0) {
          const spaceX = straightLength / qtyX;
          straightSectionChairs = range(0, qtyX).flatMap(i => {
            const x = -halfStraight + (i + 0.5) * spaceX;
            return [
              { id: `chair-top${i}`, x: x, y: -chairRadius, a: -90 },
              { id: `chair-bottom${i}`, x: x, y: chairRadius, a: 90 }
            ];
          });
        }

        // Calculate chairs for curved sections
        let curvedSectionChairs = [];

        // Number of chairs per curved section (each semicircle)
        const arcLength = Math.PI * radius;
        const chairsPerSemicircle = Math.max(3, floor(arcLength / chair.size));
        const angleStep = Math.PI / chairsPerSemicircle;

        // Adjust the starting angle to avoid overlap with straight section chairs
        const startAngleOffset = angleStep / 2;

        // Right curved section (adjusted range to avoid overlap)
        curvedSectionChairs = [
          ...range(0, chairsPerSemicircle).map(i => {
            const angle = -Math.PI / 2 + startAngleOffset + i * angleStep;
            return {
              id: `chair-right${i}`,
              x: halfStraight + chairRadius * Math.cos(angle),
              y: chairRadius * Math.sin(angle),
              a: angle * 180 / Math.PI
            };
          }),
          // Left curved section (adjusted range to avoid overlap)
          ...range(0, chairsPerSemicircle).map(i => {
            const angle = Math.PI / 2 + startAngleOffset + i * angleStep;
            return {
              id: `chair-left${i}`,
              x: -halfStraight + chairRadius * Math.cos(angle),
              y: chairRadius * Math.sin(angle),
              a: angle * 180 / Math.PI
            };
          })
        ];

        // Combine all chairs
        const chairArray = [...straightSectionChairs, ...curvedSectionChairs];

        return {
          chairArray,
          chairSpacing: { 
            x: qtyX > 0 ? straightLength / qtyX : chairRadius,
            y: chairRadius // Add y coordinate to match interface requirements
          }
        };
      },
      devicePathProps: (
        tableSize: TableSize, 
        chairSpacing: { x: number, y: number }, 
        tableBox: TableBox | null, 
        deviceFromEdge: number, 
        deviceCoverageRadius: number
      ): DevicePathProps => {
        const isHorizontal = tableSize.width <= tableSize.length;
        let devicePathCorner = ((isHorizontal ? tableSize.width : tableSize.length) / 2) - deviceFromEdge;
        let devicePathLength = (isHorizontal ? tableSize.length : tableSize.width) - 2 * deviceFromEdge - 2 * devicePathCorner;
        if (!isEmpty(tableBox) && deviceCoverageRadius * 2 >= (tableBox as TableBox).x) {
          devicePathCorner = isHorizontal ? devicePathCorner : 0;
          devicePathLength = isHorizontal ? 0 : devicePathLength;
        };
        if (!isEmpty(tableBox) && deviceCoverageRadius * 2 >= (tableBox as TableBox).y) {
          devicePathCorner = 0;
        };
        return { devicePathLength, devicePathCorner, devicePathRightCorner: devicePathCorner }
      },
    },
    boat: {
      pathProps: ({ length, width }: TableSize): TablePathProps => ({
        corner: length * .015,
        bow: length * .1,
        length: length * .97,
        width: width - length * 0.13
      }),
      chairs: ({ length, width }: TableSize = { length: 0, width: 0 }, chair: ChairConfig): ChairArrayProps => {
        const qtyX = floor(length / chair.size);
        const spaceX = qtyX ? length / qtyX : 0;
        const firstX = (spaceX - length) / 2;
        const baseY = (width - length * 0.1) / 2 + chair.clearance;
        const points: [Point, Point, Point] = [
          { x: 0, y: 0 },
          { x: length / 2, y: length * .1 },
          { x: length, y: 0 }
        ];
        const chairs = range(firstX, length / 2, spaceX).flatMap((x, i) => {
          const { curveY, angle } = quadraticBezier(x + length / 2, points);
          const y = baseY + curveY;
          const a = 90 + angle;
          return [
            { id: `chair-bottom${i}`, x: x, y: y, a: a }, 
            { id: `chair-top${i}`, x: x, y: -y, a: -a }
          ];
        });
        const short = (width - length * .13);
        const qtyY = floor(short / chair.size);
        const spaceY = short / qtyY;
        const firstY = (spaceY - short) / 2;
        const x = length / 2 + chair.clearance;
        const chairArray = chairs.concat(
          range(firstY, short / 2, spaceY).flatMap((y, i) => [
            { id: `chair-right${i}`, x: x, y: y, a: 0 }, 
            { id: `chair-left${i}`, x: -x, y: y, a: 180 }
          ])
        );
        return {
          chairArray,
          chairSpacing: { x: spaceX, y: spaceY }
        }
      },
      devicePathProps: (
        tableSize: TableSize, 
        chairSpacing: { x: number, y: number }, 
        tableBox: TableBox | null, 
        deviceFromEdge: number, 
        deviceCoverageRadius: number
      ): DevicePathProps => {
        let devicePathLength = tableSize.length - 2 * deviceFromEdge;
        let devicePathWidth = (tableSize.width - tableSize.length * .1) - 2 * deviceFromEdge;
        let devicePathBow = devicePathLength * .1;
        if (!isEmpty(tableBox) && deviceCoverageRadius * 2 >= (tableBox as TableBox).x) {
          devicePathLength = 0;
          devicePathWidth = Math.floor(devicePathWidth / chairSpacing.y) * chairSpacing.y
        };
        if (!isEmpty(tableBox) && deviceCoverageRadius * 2 >= (tableBox as TableBox).y) {
          devicePathWidth = 0;
          devicePathBow = 0;
          devicePathLength = Math.floor(devicePathLength / chairSpacing.x) * chairSpacing.x
        };
        return { devicePathLength, devicePathWidth, devicePathBow }
      },
    },
    round: {
      pathProps: ({ length }: TableSize): TablePathProps => ({
        corner: length / 2,
        length: 0,
        width: 0,
      }),
      chairs: ({ length }: TableSize = { length: 0, width: 0 }, chair: ChairConfig): ChairArrayProps => {
        const qty = floor(Math.PI * length / chair.size);
        const radius = length / 2 + chair.clearance;
        const angle = 2 * Math.PI / qty;
        const chairArray = range(0, Math.PI * 2, angle).map((a, i) => ({ 
          id: `chair-${i}`, 
          x: radius * Math.cos(a), 
          y: radius * Math.sin(a), 
          a: a * 180 / Math.PI 
        }));
        return {
          chairArray,
          chairSpacing: { x: 1, y: 1 }
        }
      },
      devicePathProps: (
        tableSize: TableSize, 
        chairSpacing: { x: number, y: number }, 
        tableBox: TableBox | null, 
        deviceFromEdge: number, 
        deviceCoverageRadius: number
      ): DevicePathProps => {
        let devicePathRightCorner = tableSize.length / 2 - deviceFromEdge;
        if (!isEmpty(tableBox) && devicePathRightCorner * 2 >= (tableBox as TableBox).x) {
          devicePathRightCorner = 0;
        };
        return { devicePathRightCorner, devicePathCorner: devicePathRightCorner }
      },
    },
    bullet: {
      pathProps: ({ length, width }: TableSize): TablePathProps => {
        const corner = Math.min(length * 0.03, width * .10);
        return {
          leftCorner: corner,
          rightCorner: width / 2,
          length: length - corner - width / 2, //  * 0.97 - width / 2 ,
          width: width - 2 * corner //- length * 0.06
        }
      },
      chairs: ({ length, width }: TableSize = { length: 0, width: 0 }, chair: ChairConfig): ChairArrayProps => {
        const radius = width / 2;
        const short = length - radius;
        const qtyX = floor(short / chair.size);
        const spaceX = short / qtyX;
        const firstX = (spaceX - length) / 2;
        const y = radius + chair.clearance;
        const chairs = range(firstX, (short - radius) / 2, spaceX).flatMap((x, i) => [
          { id: `chair-bottom${i}`, x: x, y: y, a: 90 }, 
          { id: `chair-top${i}`, x: x, y: -y, a: -90 }
        ]);
        const qtyY = floor(Math.PI * radius / chair.size);
        const chairRadius = radius + chair.clearance;
        const angle = Math.PI / qtyY;
        const baseX = length / 2 - radius;
        const chairArray = chairs.concat(
          range(angle / 2 - Math.PI / 2, Math.PI / 2, angle).map((a, i) => ({ 
            id: `chair-right${i}`, 
            x: baseX + chairRadius * Math.cos(a), 
            y: chairRadius * Math.sin(a), 
            a: a * 180 / Math.PI 
          }))
        );
        return {
          chairArray,
          chairSpacing: { x: spaceX, y: 1 }
        };
      },
      devicePathProps: (
        tableSize: TableSize,
        chairSpacing: { x: number, y: number },
        tableBox: TableBox | null,
        deviceFromEdge: number,
        deviceCoverageRadius: number,
        legWidth?: number,
        isExpansionTableMic = false
      ): DevicePathProps => {
        let devicePathRightCorner = tableSize.width / 2 - deviceFromEdge;
        const devicePathLength = Math.max(0, tableSize.length - 2 * deviceFromEdge - devicePathRightCorner);
        // The bullet's "redundant return leg" reasoning (calcExpansionTableMicPlacement /
        // BAR_TABLETOP_MIC_SPEC_CHANGES.md C5) assumes the far end collapses to a SINGLE point on
        // the centreline, so its single-chain tiling can walk out-leg -> far point -> return-leg as
        // one flat line. That collapse used to be gated on `deviceCoverageRadius * 2 >= tableBox.y`
        // (same "does one mic already cover the full width" heuristic the ceiling/standalone mic
        // path uses) — but for the expansion TABLE mic that heuristic can be false (e.g. Poor
        // acoustics' small TTM-X radius against a 6 ft wide table), leaving a genuine semicircular
        // cap of radius `devicePathRightCorner` at the far end instead of a point. The placement
        // algorithm has no two-leg handling for bullet (unlike U/V/keystone), so a mic forced onto
        // that far corner (the mandatory `tFarCorner`/`isChainFarEnd` slot) samples a point on the
        // curve — off the centreline — instead of the assumed single far point. Always collapse for
        // the expansion table mic, independent of the coverage-radius heuristic.
        if (isExpansionTableMic || (!isEmpty(tableBox) && deviceCoverageRadius * 2 >= (tableBox as TableBox).y)) {
          devicePathRightCorner = 0;
        };
        return { devicePathLength, devicePathRightCorner }
      },
    },
    keystone: {
      maxSlope: .3,
      pathProps: ({ length, width }: TableSize): TablePathProps => ({
        corner: length * 0.015,
        length: length * .97,
        width: width - length * 0.03,
      }),
      chairs: ({ length, width }: TableSize = { length: 0, width: 0 }, chair: ChairConfig, slope = 0): ChairArrayProps => {
        const tapper = length * slope * .94;
        const qtyX = floor(length / chair.size);
        const spaceX = qtyX ? length / qtyX : 0;
        const firstX = (spaceX - length) / 2 + chair.size * slope / 4;
        const baseY = (width - tapper) / 2 + chair.clearance;
        const angle = slope * 180 / Math.PI;
        const chairs = range(firstX, length / 2, spaceX).flatMap((x, i) => {
          const y = baseY - x * slope;
          const a = 90 - angle;
          return [
            { id: `chair-bottom${i}`, x: x, y: y, a: a }, 
            { id: `chair-top${i}`, x: x, y: -y, a: -a }
          ];
        });
        const short = width - 2 * tapper;
        const qtyY = floor(short / chair.size);
        const spaceY = qtyY ? short / qtyY : 0;
        const firstY = (spaceY - short) / 2;
        const x = length / 2 + chair.clearance;
        const chairArray = chairs.concat(
          range(firstY, short / 2, spaceY).map((y, i) => ({ 
            id: `chair-right${i}`, 
            x: x, 
            y: y, 
            a: 0 
          }))
        );
        return {
          chairArray,
          chairSpacing: { x: spaceX, y: spaceY }
        };
      },
      devicePathProps: (
        tableSize: TableSize,
        chairSpacing: { x: number, y: number },
        tableBox: TableBox | null,
        deviceFromEdge: number,
        deviceCoverageRadius: number,
        legWidth?: number,
        isExpansionTableMic = false
      ): DevicePathProps => {
        let devicePathLength = tableSize.length - 2 * deviceFromEdge;
        let devicePathWidth = tableSize.width - 2 * deviceFromEdge;
        // Collapse the tapered path to a flat centreline when a single mic already covers the
        // full seating width (deviceCoverageRadius * 2 >= tableBox.y). Tracing two converging
        // edges only pays off when the table is wide enough to need two rows of mics.
        //
        // This matters because the open endTable path begins and ends at the two corners of the
        // SAME wide end. calculateMicPlacement anchors one mic per endpoint (spacing =
        // pathLength / (qty - 1)), so at a low mic count both mics land on identical x and the
        // narrow/head end gets no coverage at all.
        //
        // Skipped ONLY for the expansion table mic, which needs the taper to find each arm
        // (calcExpansionTableMicPlacement; BAR_TABLETOP_MIC_SPEC_CHANGES.md C5). It must NOT be
        // keyed off isTableMic: #micPath is always built with isTableMic=true for the edge inset,
        // so doing that disabled the collapse for every mic consumer, ceiling mics included.
        if (!isEmpty(tableBox) && !isExpansionTableMic && deviceCoverageRadius * 2 >= (tableBox as TableBox).y) {
          devicePathWidth = 0;
          devicePathLength = Math.floor(devicePathLength / chairSpacing.x) * chairSpacing.x
        };
        return { devicePathLength, devicePathWidth }
      },
    },
    ushape: {
      notched: true,
      pathProps: ({ length, width }: TableSize): TablePathProps => ({
        corner: Math.min(length * 0.05, width * 0.015), // Threshold beyond max room size (100')
        length: length * 0.94,
        width: width - length * 0.06,
      }),
      chairs: (tableSize: TableSize, chair: ChairConfig): ChairArrayProps => {
        // Use the same adjusted dimensions as the table path
        const adjustedTableSize = {
          length: tableSize.length * 0.94,
          width: tableSize.width - tableSize.length * 0.06
        };

        // Use rectangle chair layout with adjusted dimensions and filter out left chairs (chairs in the opening)
        const { chairArray: rectangleChairs, chairSpacing } = MuiTableConstants.tables.rectangle.chairs(adjustedTableSize, chair);
        const chairArray = rectangleChairs.filter(chair => !chair.id.includes('-left'));
        return { chairArray, chairSpacing };
      },
      devicePathProps: (
        tableSize: TableSize,
        chairSpacing: { x: number, y: number },
        tableBox: TableBox | null,
        deviceFromEdge: number,
        deviceCoverageRadius: number,
        legWidth?: number
      ): DevicePathProps => {
        const adjustedLength = tableSize.length * 0.94;
        const adjustedWidth = tableSize.width - tableSize.length * 0.06;
        let devicePathLength = adjustedLength - 2 * deviceFromEdge;
        let devicePathWidth = adjustedWidth - 2 * deviceFromEdge;
        if (adjustedWidth <= 2 * (legWidth || 0)) {
          devicePathWidth = 0;
          devicePathLength = Math.floor(devicePathLength / chairSpacing.x) * chairSpacing.x
        };
        return { devicePathLength, devicePathWidth }
      },
    },
    vshape: {
      notched: true,
      maxSlope: .63,
      pathProps: ({ length, width }: TableSize): TablePathProps => ({
        corner: length * 0.0075,
        length: length * .985,
        width: width - length * 0.015,
      }),
      chairs: (tableSize: TableSize, chair: ChairConfig, slope?: number): ChairArrayProps => 
        MuiTableConstants.tables.keystone.chairs(tableSize, chair, slope),
      devicePathProps: (
        tableSize: TableSize, 
        chairSpacing: { x: number, y: number }, 
        tableBox: TableBox | null, 
        deviceFromEdge: number, 
        deviceCoverageRadius: number, 
        legWidth?: number, 
        isExpansionTableMic = false
      ): DevicePathProps => {
        let devicePathLength = tableSize.length - 2 * deviceFromEdge;
        let devicePathWidth = tableSize.width - 2 * deviceFromEdge;
        if (!isEmpty(tableBox) && !isExpansionTableMic && deviceCoverageRadius * 2 >= (tableBox as TableBox).y) {
          devicePathWidth = 0;
          devicePathLength = Math.floor(devicePathLength / chairSpacing.x) * chairSpacing.x
        };
        return { devicePathLength, devicePathWidth }
      },
    },
  },
  chair: {
    size: 0.762, // size of chair 30" in meters
    clearance: 0.1905, // 25% chair clearance from table .25 of chair is under table
    state: (ghosted: boolean, filled: boolean, customColor?: string, customStroke?: string) => {
      // For acoustics/microphones/speakers/cameras sections in light mode: all grey 
      // For floorplan/ceiling/furniture views: use customColor for all parts
      
      return {
        border_fill: filled ? (customColor || '#696969') : 'none',
        chair_top_fill: filled ? (customColor || '#696969') : 'none', // Grey backrest
        chair_seat_fill: filled ? (customColor || '#696969') : 'none', // Grey seat  
        icon_stroke: ghosted ? (filled ? '#9ca3af' : 'none') : (customStroke || '#696969'),
        opacity: ghosted ? .2 : 1,
        dash: ghosted ? 4 : 0
      }
    }
  },
  deviceToEdge: (isTableMic = true, index = 2) => isTableMic ? 0.4 : [0.238, 0.538, 0.938, 1.448, 1.738][index]
};

export default MuiTableConstants;
